import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { checkInUser, checkOutUser } from "../../redux/actions/attendanceActions";
import logo from "../../images/wb_logo.png";
import { toast } from "react-hot-toast";

const AttendModal = ({ isOpen, onClose, type = "checkin" }) => {
  const dispatch = useDispatch();
  const videoRef = useRef(null);
  const [currentStep, setCurrentStep] = useState("options"); // "options", "camera", "processing"
  const [selectedAttendanceType, setSelectedAttendanceType] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Reset states when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (type === "checkin") {
        setCurrentStep("options");
      } else {
        setCurrentStep("camera");
        openCamera();
      }
      setSelectedAttendanceType(null);
      setIsProcessing(false);
      
      // Prevent background scrolling on iOS
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      // Clean up when modal closes
      stopCamera();
      setCurrentStep("options");
      setSelectedAttendanceType(null);
      setIsProcessing(false);
      
      // Restore background scrolling
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
  }, [isOpen, type]);

  const openCamera = async (attendanceType = null) => {
    if (attendanceType) {
      setSelectedAttendanceType(attendanceType);
    }
    
    try {
      setCurrentStep("camera");
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 640 }, 
          height: { ideal: 480 },
          facingMode: "user" 
        } 
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Camera access error:", err);
      toast.error("Camera access denied. Please allow camera permission.");
      if (type === "checkin") {
        setCurrentStep("options");
      }
    }
  };

  const captureImage = () => {
    if (!videoRef.current || isProcessing) return;
    
    setIsProcessing(true);
    setCurrentStep("processing");
    
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const context = canvas.getContext("2d");
    context.drawImage(videoRef.current, 0, 0);
    
    canvas.toBlob((blob) => {
      const file = new File(
        [blob], 
        type === "checkin" ? "checkin.jpg" : "checkout.jpg", 
        { type: "image/jpeg" }
      );
      
      stopCamera();
      handleSubmit(file);
    }, "image/jpeg", 0.8);
  };

  const stopCamera = () => {
    const stream = videoRef.current?.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  const getAttendanceConfig = (attendanceType) => {
    const configs = {
      office: {
        status: "present",
        remarks: "inside",
      },
      within_ulb: {
        status: "pending", 
        remarks: "outside",
      },
      outside_ulb: {
        status: "pending",
        remarks: "other",
      }
    };
    return configs[attendanceType] || configs.office;
  };

  const handleSubmit = async (photo) => {
    const getCurrentPosition = () => {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error("Geolocation is not supported"));
          return;
        }
        
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          { 
            enableHighAccuracy: true, 
            timeout: 10000, 
            maximumAge: 60000 
          }
        );
      });
    };

    try {
      const position = await getCurrentPosition();
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      const currentTime = new Date().toLocaleTimeString("en-GB", {
        hour12: false,
      });

      const formData = new FormData();
      
      if (type === "checkin") {
        const config = getAttendanceConfig(selectedAttendanceType);
        formData.append("check_in", currentTime);
        formData.append("status", config.status);
        formData.append("check_in_latitude", lat);
        formData.append("check_in_longitude", lng);
        formData.append("check_in_address", "N/A");
        formData.append("remarks", config.remarks);
        formData.append("check_in_photo", photo);
        
        const response = await dispatch(checkInUser(formData));
        handleResponse(response, "Check-in");
        
      } else {
        formData.append("check_out", currentTime);
        formData.append("check_out_latitude", lat);
        formData.append("check_out_longitude", lng);
        formData.append("check_out_address", "N/A");
        formData.append("check_out_photo", photo);
        
        const response = await dispatch(checkOutUser(formData));
        handleResponse(response, "Check-out");
      }
      
    } catch (error) {
      console.error("Submission error:", error);
      if (error.code === error.PERMISSION_DENIED) {
        toast.error("Location access denied. Please enable location services.");
      } else {
        toast.error(`Something went wrong during ${type === "checkin" ? "check-in" : "check-out"}.`);
      }
      setIsProcessing(false);
      setCurrentStep("camera");
    }
  };

  const handleResponse = (response, actionType) => {
    console.log(`${actionType} response:`, response);
    
    if (response?.meta?.status) {
      toast.success(response.meta.message || `${actionType} successful!`);
      handleClose();
    } else {
      toast.error(response?.meta?.message || `${actionType} failed.`);
      setIsProcessing(false);
      setCurrentStep("camera");
    }
  };

  const handleClose = () => {
    stopCamera();
    setCurrentStep("options");
    setSelectedAttendanceType(null);
    setIsProcessing(false);
    onClose();
  };

  const goBackToOptions = () => {
    stopCamera();
    setCurrentStep("options");
    setSelectedAttendanceType(null);
  };

  const getAttendanceTypeLabel = (attendanceType) => {
    const labels = {
      office: "Office Duty",
      within_ulb: "Official Visit Within ULB Jurisdiction", 
      outside_ulb: "Official Visit Outside ULB Jurisdiction"
    };
    return labels[attendanceType] || "";
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex flex-col">
      {/* iOS-friendly scrollable container */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        <div className="min-h-full flex items-center justify-center p-4 py-8">
          <div className="bg-white rounded-xl w-full max-w-md relative text-center">
            <div className="sticky top-0 bg-white rounded-t-xl z-10 px-6 pt-4 pb-2">
              <button
                onClick={handleClose}
                disabled={isProcessing}
                className="absolute top-3 right-4 text-black text-2xl font-bold hover:text-gray-600 disabled:opacity-50"
              >
                &times;
              </button>
              <img src={logo} alt="logo" className="mx-auto w-16" />
            </div>

            <div className="px-6 pb-6">
              {/* Step 1: Show options for check-in */}
              {currentStep === "options" && type === "checkin" && (
                <>
                  <h3 className="text-green-600 font-semibold text-lg mb-6">
                    Select from where are you CLOCK IN
                  </h3>
                  <div className="space-y-3">
                    <button
                      className="w-full bg-lime-500 hover:bg-lime-600 text-white py-3 rounded-md font-semibold transition-colors"
                      onClick={() => openCamera("office")}
                    >
                      Office Duty
                    </button>
                    <button
                      onClick={() => openCamera("within_ulb")}
                      className="w-full bg-sky-500 hover:bg-sky-600 text-white py-3 rounded-md font-semibold transition-colors"
                    >
                      Official Visit Within ULB Jurisdiction
                    </button>
                    <button
                      onClick={() => openCamera("outside_ulb")}
                      className="w-full bg-red-500 hover:bg-red-600 text-white py-3 rounded-md font-semibold transition-colors"
                    >
                      Official Visit Outside ULB Jurisdiction
                    </button>
                  </div>
                </>
              )}

              {/* Step 2: Camera capture */}
              {currentStep === "camera" && (
                <>
                  <h3 className="text-green-600 font-semibold text-lg mb-4">
                    {type === "checkin" 
                      ? "CLOCK IN - Capture your photo" 
                      : "CLOCK OUT - Capture your photo"}
                  </h3>
                  
                  {type === "checkin" && selectedAttendanceType && (
                    <p className="text-sm text-gray-600 mb-3 bg-gray-100 p-2 rounded">
                      Selected: {getAttendanceTypeLabel(selectedAttendanceType)}
                    </p>
                  )}
                  
                  <div className="mb-6">
                    <video 
                      ref={videoRef} 
                      autoPlay 
                      playsInline
                      muted
                      className="w-full max-h-64 rounded-lg bg-gray-200" 
                    />
                  </div>
                  
                  <div className="flex gap-2 justify-center">
                    <button
                      onClick={captureImage}
                      disabled={isProcessing}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition-colors disabled:opacity-50"
                    >
                      📷 Capture & Submit
                    </button>
                    <button
                      onClick={type === "checkin" ? goBackToOptions : handleClose}
                      disabled={isProcessing}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors disabled:opacity-50"
                    >
                      {type === "checkin" ? "← Back" : "Cancel"}
                    </button>
                  </div>
                </>
              )}

              {/* Step 3: Processing */}
              {currentStep === "processing" && (
                <>
                  <h3 className="text-green-600 font-semibold text-lg mb-6">
                    Processing {type === "checkin" ? "Check-in" : "Check-out"}...
                  </h3>
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Please wait while we process your {type === "checkin" ? "check-in" : "check-out"}...
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendModal;